import { CompliantType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Account } from '~hooks/useAccount/models';
import { StagedComplianceDoc } from '~hooks/useComplianceDocuments/checklist';
import { useCreateStagedComplianceDocuments } from '~hooks/useComplianceDocuments/useCreateStagedComplianceDocuments';
import useReportSaving from '~hooks/useReportSaving/useReportSaving';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import VendorAccountForm, { VendorAccountFormHandler } from './VendorAccountForm';

interface VendorAccountFormDialogWrapperProps {
  account?: Account;
  isOpen: boolean;
  onAccountCreated?: () => void;
  onAccountUpdated?: () => void;
  onClose: () => void;
  onSavingChange?: (isSaving: boolean) => void;
}

export default function VendorAccountFormDialogWrapper({
  account,
  isOpen,
  onAccountCreated,
  onAccountUpdated,
  onClose,
  onSavingChange,
}: VendorAccountFormDialogWrapperProps) {
  const { toasterStore, userStore } = useStores();
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    getEffectiveUserCompanyId(userStore),
  );
  const { createStagedDocs } = useCreateStagedComplianceDocuments(selectedCompanyId);

  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [hasApiResponseErrors, setHasApiResponseErrors] = useState(false);
  const [isComplianceEditing, setIsComplianceEditing] = useState(false);
  const [stagedComplianceDocs, setStagedComplianceDocs] = useState<StagedComplianceDoc[]>(
    [],
  );

  useReportSaving(isSavingAccount, onSavingChange);

  const vendorAccountFormRef = useRef<VendorAccountFormHandler>(null);

  const isEditing = Boolean(account?.id);

  useEffect(() => {
    return () => {
      vendorAccountFormRef.current?.resetForm();
      setStagedComplianceDocs([]);
      setIsComplianceEditing(false);
    };
  }, [isOpen]);

  return (
    <Dialog
      isOpen={isOpen || false}
      maxWidth="lg"
      preventCloseOnBackdropClick
      preventCloseOnEscapeKeyDown
    >
      <DialogHeader onClose={onClose}>
        {isEditing ? t('account.update_account') : t('account.create_account')}
      </DialogHeader>

      <DialogContent sx={(theme) => ({ bgcolor: theme.semantic.surfaceSunken })}>
        <VendorAccountForm
          ref={vendorAccountFormRef}
          account={account}
          onAccountCreated={async (account: Account) => {
            // Create staged compliance documents for the new account
            const { failed } = await createStagedDocs(
              CompliantType.ACCOUNT,
              account.id,
              stagedComplianceDocs,
            );
            if (failed > 0) {
              toasterStore.push(
                alert(
                  t('error_messages.compliance_document_types.partial_save_failure'),
                  AlertTypes.warn,
                ),
              );
            }

            toasterStore.push(
              alert(
                t('account.account_created', { name: account.name }),
                AlertTypes.success,
              ),
            );

            onAccountCreated?.();
            onClose();
          }}
          onAccountUpdated={(account: Account) => {
            toasterStore.push(
              alert(
                t('account.account_updated', { name: account.name }),
                AlertTypes.success,
              ),
            );

            onAccountUpdated?.();
            onClose();
          }}
          onApiResponseErrorsChanged={setHasApiResponseErrors}
          onComplianceEditingChange={setIsComplianceEditing}
          onSavingStatusChanged={setIsSavingAccount}
          onSelectedCompanyChange={setSelectedCompanyId}
          onStagedComplianceDocsChange={setStagedComplianceDocs}
        />
      </DialogContent>

      <DialogActions>
        {hasApiResponseErrors && (
          <ShakyErrorIndicator
            message={t('administration.users.user_form.invalid_data_error_messages')}
          />
        )}

        <SmallButton
          color="secondary"
          disabled={isSavingAccount || isComplianceEditing}
          onClick={onClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          disabled={isComplianceEditing}
          loading={isSavingAccount}
          onClick={() => vendorAccountFormRef.current?.submitForm()}
        >
          {t('actions.save')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
